import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ReceiptText, TicketCheck, RefreshCw } from "lucide-react";
import { MaskedEmail } from "@/components/MaskedEmail";

const STATUS_STYLE = {
  paid: "bg-[rgba(34,255,102,0.1)] text-[color:var(--ufr-green)] border-[color:var(--ufr-green)]/30",
  initiated: "bg-amber-400/10 text-amber-300 border-amber-400/30",
  failed: "bg-red-400/10 text-red-300 border-red-400/30",
};

export default function AdminOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/orders");
      setOrders(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const markPaid = async (o) => {
    if (!window.confirm(
      `Issue tickets for this order?\n\nBuyer: ${o.user_email}\nAmount: £${(o.amount || 0).toFixed(2)}\n\nONLY do this after confirming in your Nochex control panel that this payment was actually received.`
    )) return;
    setBusy(o.id);
    try {
      await api.post(`/admin/orders/${o.id}/mark-paid`);
      toast.success("Tickets issued — buyer has been emailed their ticket numbers");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div data-testid="admin-orders-page">
      <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--ufr-green)] font-bold">Payments</div>
          <h1 className="font-display text-4xl md:text-5xl text-white mt-2">Orders</h1>
          <p className="text-white/50 text-sm mt-2 max-w-2xl">
            Every checkout attempt. <span className="text-amber-300">Amber "started"</span> orders never confirmed —
            if the money shows in your Nochex account, press <strong className="text-white">Issue tickets</strong> to
            grant their entries and send the confirmation email.
          </p>
        </div>
        <button onClick={load} className="btn-ufr-ghost px-4 py-2.5 text-xs inline-flex items-center gap-2" data-testid="orders-refresh-btn">
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {loading ? (
        <div className="text-white/50">Loading…</div>
      ) : orders.length === 0 ? (
        <div className="text-center text-white/50 py-16 border border-dashed border-white/10 rounded-md">No orders yet.</div>
      ) : (
        <div className="ufr-card rounded-md overflow-hidden">
          <div className="hidden md:grid grid-cols-12 px-4 py-3 text-[10px] uppercase tracking-[0.18em] text-white/60 font-bold border-b border-white/5">
            <div className="col-span-3">Date / Buyer</div>
            <div className="col-span-4">Items</div>
            <div className="col-span-1 text-right">Amount</div>
            <div className="col-span-2 text-center">Status</div>
            <div className="col-span-2 text-right">Action</div>
          </div>
          {orders.map((o) => {
            const status = o.payment_status || "initiated";
            const badge = STATUS_STYLE[status] || STATUS_STYLE.initiated;
            const label = status === "paid" ? "Paid" : status === "failed" ? "Failed" : "Started";
            return (
              <div key={o.id} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-white/5 text-sm items-center" data-testid={`order-row-${o.id}`}>
                <div className="col-span-12 md:col-span-3">
                  <div className="text-white/50 text-xs">{o.created_at ? new Date(o.created_at).toLocaleString() : "—"}</div>
                  <div className="text-white/85 text-xs mt-0.5"><MaskedEmail email={o.user_email} testid={`order-email-${o.id}`} /></div>
                </div>
                <div className="col-span-12 md:col-span-4 text-white/70 text-xs">
                  {(o.items || []).map((it, i) => (
                    <div key={i}>{it.tickets}× {it.title}</div>
                  ))}
                  {o.discount_code && <div className="text-[color:var(--ufr-green)]">Code: {o.discount_code}</div>}
                </div>
                <div className="col-span-4 md:col-span-1 md:text-right font-display text-[color:var(--ufr-green)]">£{(o.amount || 0).toFixed(2)}</div>
                <div className="col-span-4 md:col-span-2 md:text-center">
                  <span className={`inline-block px-2 py-0.5 text-[10px] uppercase tracking-widest font-bold border rounded-sm ${badge}`} data-testid={`order-status-${o.id}`}>
                    {label}{o.manually_reconciled_by ? " (manual)" : ""}
                  </span>
                </div>
                <div className="col-span-4 md:col-span-2 md:text-right">
                  {!o.entries_granted && o.provider !== "free" && (
                    <button
                      onClick={() => markPaid(o)}
                      disabled={busy === o.id}
                      className="btn-ufr-primary px-3 py-1.5 text-[10px] inline-flex items-center gap-1"
                      data-testid={`order-mark-paid-${o.id}`}
                    >
                      <TicketCheck size={12} /> {busy === o.id ? "Issuing…" : "Issue tickets"}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
